import React, { useEffect, useState } from 'react';
import { hrmsListOffers, hrmsSendOffer, hrmsRevokeOffer } from '../../lib/hrmsApi';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { Send, Ban, Download, Copy } from 'lucide-react';
import { generateOfferPdf } from '../../lib/offerPdf';

const STATUS_COLORS = {
  draft: 'bg-slate-500', sent: 'bg-blue-500', accepted: 'bg-emerald-500',
  rejected: 'bg-red-500', expired: 'bg-amber-500', revoked: 'bg-slate-400',
};

const OffersPage = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const load = async () => setRows(await hrmsListOffers().catch(() => []));
  useEffect(() => { load(); }, []);

  const send = async (o) => {
    if (!window.confirm(`Send offer ${o.offer_id} to ${o.candidate_email}?`)) return;
    try {
      await hrmsSendOffer(o.id);
      toast({ title: 'Offer sent', description: `Email delivered to ${o.candidate_email}` });
      load();
    } catch (err) { toast({ title: 'Send failed', description: err?.response?.data?.detail || '' }); }
  };
  const revoke = async (o) => {
    if (!window.confirm(`Revoke offer ${o.offer_id}?`)) return;
    await hrmsRevokeOffer(o.id);
    toast({ title: 'Offer revoked' });
    load();
  };
  const copyLink = (o) => {
    const link = `${window.location.origin}/offer/${o.token}`;
    navigator.clipboard.writeText(link);
    toast({ title: 'Link copied', description: link });
  };
  const downloadPdf = (o) => {
    generateOfferPdf(o);
  };

  return (
    <div data-testid="offers-page">
      <h1 className="text-2xl font-extrabold">Offer letters</h1>
      <p className="text-sm text-slate-500 mt-1">Create offers from a candidate card in the Pipeline. Send, revoke and download PDFs here.</p>

      <div className="mt-6 rounded-2xl border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left px-4 py-3">Offer ID</th>
            <th className="text-left px-4 py-3">Candidate</th>
            <th className="text-left px-4 py-3">Role</th>
            <th className="text-left px-4 py-3">CTC</th>
            <th className="text-left px-4 py-3">Joining</th>
            <th className="text-left px-4 py-3">Status</th>
            <th className="text-left px-4 py-3">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No offers yet. Move a candidate to "Selected" in the Pipeline and click "Create offer".</td></tr>}
            {rows.map((o) => (
              <tr key={o.id} className="border-t align-top" data-testid={`offer-row-${o.offer_id}`}>
                <td className="px-4 py-3 font-mono text-xs">{o.offer_id}</td>
                <td className="px-4 py-3 font-medium">{o.candidate_name}<div className="text-xs text-slate-500">{o.candidate_email}</div></td>
                <td className="px-4 py-3">{o.designation}<div className="text-xs text-slate-500">{o.department}</div></td>
                <td className="px-4 py-3">{o.ctc}</td>
                <td className="px-4 py-3">{o.joining_date}</td>
                <td className="px-4 py-3"><Badge className={STATUS_COLORS[o.status] || 'bg-slate-500'}>{o.status}</Badge></td>
                <td className="px-4 py-3 space-x-1">
                  {o.status === 'draft' && <Button onClick={() => send(o)} className="h-8 px-2 bg-blue-600 hover:bg-blue-700" data-testid={`offer-send-${o.offer_id}`}><Send className="h-3.5 w-3.5 mr-1" /> Send</Button>}
                  {o.status === 'sent' && <Button variant="outline" onClick={() => copyLink(o)} className="h-8 px-2" data-testid={`offer-link-${o.offer_id}`}><Copy className="h-3.5 w-3.5 mr-1" /> Copy link</Button>}
                  <Button variant="outline" onClick={() => downloadPdf(o)} className="h-8 px-2" data-testid={`offer-pdf-${o.offer_id}`}><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
                  {(o.status === 'sent' || o.status === 'draft') && <Button variant="outline" onClick={() => revoke(o)} className="h-8 px-2 text-red-600 border-red-200"><Ban className="h-3.5 w-3.5" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OffersPage;
